import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CallIcon from '@mui/icons-material/Call';
import { ACCENT, ACCENT_SOFT_BG, HEADING_INK, BODY_INK } from './ai-chat-intro';
import { ChatDoc, ChatStatus } from '../../pages/app/support-agent/support-agent-api';
import { SUPPORT_HOURS_LABEL } from './ai-chat-support-hours';

const RESTART_ICON_BG = '#FBE5E6';
const RESTART_ICON_COLOR = '#B42B2B';
const EMERGENCY_BORDER = '#D93A2B';
const EMERGENCY_BG = '#FFF5F4';
const EMERGENCY_RED = '#B42B2B';

/**
 * Escalation and resolve copy/UI approved by Carlos (10/7), ported from the
 * mobile first-run design (`~/.buzz/RESEARCH/AI_CHAT_MOBILE_DESIGN_2026_10_07/
 * mobile_chat_first_open.html`, `confirmHuman`/`handoff`/`confirmRestart`
 * blocks) to web. Two web-specific differences from that design, both
 * Carlos's explicit calls in the same thread:
 *  - No "Emergency? Use the SOS slider on the Map" line — the Safety Hub has
 *    no SOS slider (web drops the line entirely rather than substituting one).
 *  - "Keep using the app. We'll notify you." (push) becomes "Keep using the
 *    Safety Hub. Replies show up here." (web has no push; the badge on the
 *    chat button is the only signal).
 */

// Esc is handled by the chat panel's own onKeyDown (ai-chat-widget.tsx
// handlePanelKeyDown), not here — these sheets render AS CHILDREN of that
// panel, so a window- or document-level listener in here would race the
// panel's existing Escape-to-minimize handler for the very same keypress,
// and neither has a reliable way to know which should win. Routing through
// the panel instead lets it pick the right sheet's own cancel semantics
// (the close-confirm sheet's backdrop/Esc deliberately differs from its
// "Keep open" button — see dismissCloseConfirm there) with a single source
// of truth for "what's open right now."

/** A click that lands on the backdrop itself (not bubbled from the sheet) cancels, unless a request is already in flight. */
function backdropClickCancels(confirming: boolean, onCancel: () => void) {
  return () => {
    if (!confirming) onCancel();
  };
}

const AMBER_BG = '#FFF8EC';
const AMBER_BORDER = '#F3DDB0';
const AMBER_DOT = '#F2A33A';
const HOURS_BOX_BG = '#F6FAFE';
const HOURS_BOX_BORDER = '#CFE2F3';
const MUTED_INK = '#7A8794';

// ── "Talk to a person?" confirm sheet ──────────────────────────────────────

export const AI_CHAT_CONFIRM_HUMAN_TITLE = 'Talk to a person?';
export const AI_CHAT_CONFIRM_HUMAN_BODY = 'The next available support rep will reply here.';
export const AI_CHAT_CONFIRM_HUMAN_KEEP_USING = 'Keep using the Safety Hub. Replies show up here.';
export const AI_CHAT_CONFIRM_HUMAN_YES = 'Yes, talk to a person';
export const AI_CHAT_CONFIRM_HUMAN_CANCEL = 'Keep chatting with the Assistant';

interface ConfirmHumanSheetProps {
  openState: string;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Rendered as a sheet INSIDE the chat panel (Pollen's brief, 10/7) — not a
 * separate MUI Dialog — so it sits below the header exactly like the design's
 * mobile bottom sheet, rather than as a floating modal disconnected from the
 * chat it's about.
 */
export const ConfirmHumanSheet: React.FC<ConfirmHumanSheetProps> = ({ openState, confirming, onConfirm, onCancel }) => (
  <Box
    onClick={backdropClickCancels(confirming, onCancel)}
    sx={{
      position: 'absolute',
      inset: 0,
      bgcolor: 'rgba(5,12,20,0.55)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      zIndex: 3,
    }}
  >
    <Box
      role="dialog"
      aria-label={AI_CHAT_CONFIRM_HUMAN_TITLE}
      onClick={(e) => e.stopPropagation()}
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: '16px 16px 0 0',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        boxShadow: '0 -16px 48px rgba(0,0,0,0.35)',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '28px',
          bgcolor: ACCENT_SOFT_BG,
          color: ACCENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SupportAgentIcon sx={{ fontSize: 28 }} />
      </Box>
      <Typography sx={{ fontSize: 20, fontWeight: 600, color: HEADING_INK, lineHeight: 1.25 }}>
        {AI_CHAT_CONFIRM_HUMAN_TITLE}
      </Typography>
      <Typography sx={{ fontSize: 14.5, lineHeight: 1.5, color: BODY_INK }}>{AI_CHAT_CONFIRM_HUMAN_BODY}</Typography>
      <Box
        sx={{
          fontSize: 13.5,
          lineHeight: 1.5,
          color: BODY_INK,
          bgcolor: HOURS_BOX_BG,
          border: `1.5px solid ${HOURS_BOX_BORDER}`,
          borderRadius: '12px',
          p: '10px 12px',
          display: 'flex',
          gap: 1.25,
          alignItems: 'flex-start',
        }}
      >
        <AccessTimeIcon sx={{ fontSize: 18, color: ACCENT, mt: '2px', flexShrink: 0 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography component="span" sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: 'inherit' }}>
            <Box component="strong" sx={{ fontWeight: 600, color: HEADING_INK }}>
              Support hours:
            </Box>{' '}
            {SUPPORT_HOURS_LABEL} · {openState}
          </Typography>
          <Typography component="span" sx={{ color: MUTED_INK, fontSize: 12.5 }}>
            {AI_CHAT_CONFIRM_HUMAN_KEEP_USING}
          </Typography>
        </Box>
      </Box>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={confirming}
        sx={{
          height: 52,
          mt: 0.75,
          borderRadius: '14px',
          bgcolor: ACCENT,
          '&:hover': { bgcolor: ACCENT },
          fontSize: 15,
          fontWeight: 600,
          textTransform: 'none',
        }}
      >
        {confirming ? 'Connecting…' : AI_CHAT_CONFIRM_HUMAN_YES}
      </Button>
      <Button
        onClick={onCancel}
        disabled={confirming}
        sx={{ height: 48, color: '#5B6B7C', fontSize: 15, fontWeight: 500, borderRadius: '12px', textTransform: 'none' }}
      >
        {AI_CHAT_CONFIRM_HUMAN_CANCEL}
      </Button>
    </Box>
  </Box>
);

// ── Post-escalation handoff banner ─────────────────────────────────────────

export const AI_CHAT_HANDOFF_LEAD = 'Sent to support.';

export function handoffBannerRest(openState: string): string {
  return `Waiting for the next available rep. Support hours: ${SUPPORT_HOURS_LABEL} · ${openState}. You can keep using the Safety Hub.`;
}

export const HandoffBanner: React.FC<{ openState: string }> = ({ openState }) => (
  <Box
    sx={{
      bgcolor: AMBER_BG,
      border: `1.5px solid ${AMBER_BORDER}`,
      borderRadius: '12px',
      p: '10px 12px',
      display: 'flex',
      gap: 1,
      alignItems: 'flex-start',
    }}
  >
    <Box sx={{ width: 8, height: 8, borderRadius: '4px', bgcolor: AMBER_DOT, mt: '5px', flexShrink: 0 }} />
    <Typography sx={{ fontSize: 12.5, lineHeight: 1.45, color: BODY_INK }}>
      <Box component="strong" sx={{ fontWeight: 600, color: HEADING_INK }}>
        {AI_CHAT_HANDOFF_LEAD}
      </Box>{' '}
      {handoffBannerRest(openState)}
    </Typography>
  </Box>
);

// ── Resolved copy, by who/what closed the chat ─────────────────────────────

/**
 * `resolvedBy` is the chat doc's own field (ChatDoc.resolvedBy) — which of
 * the 4 ways a chat can close. `resolvedAt` (when set) appends the close
 * time in local time; an unrecognized/missing `resolvedBy` falls back to the
 * generic "Resolved" (Carlos/Pollen, 2026-10-13 — the footer below this line
 * already has its own "Start a new chat" button, so this is just the label
 * now, same text as the app's pill: see support_chat_copy.dart on frontend).
 */
export function resolvedCopy(resolvedBy: ChatDoc['resolvedBy'], resolvedAt?: string): string {
  const label =
    resolvedBy === 'ai'
      ? 'Resolved by AI'
      : resolvedBy === 'staff'
        ? 'Resolved by Support'
        : resolvedBy === 'customer'
          ? 'Closed by you'
          : resolvedBy === 'inactivity'
            ? 'Closed for inactivity'
            : 'Resolved';
  if (!resolvedAt) return label;
  const time = new Date(resolvedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${label} · ${time}`;
}

// ── "Start a new chat?" confirm dialog content ─────────────────────────────

export const AI_CHAT_CONFIRM_RESTART_TITLE = 'Start a new chat?';
export const AI_CHAT_CONFIRM_RESTART_CONFIRM = 'End and start new';
export const AI_CHAT_CONFIRM_RESTART_CANCEL = 'Keep this chat';

/**
 * The "a new request will go to the back of the line" sentence is only true
 * once a support person is actually involved — while still talking to the
 * Assistant there is no line, and showing it then could confuse or alarm the
 * customer (Pollen/Carlos, 10/7).
 */
export function confirmRestartBody(status: ChatStatus): string {
  if (status === 'escalated') {
    return "This will end your current conversation. If a support person is helping you, they won't be able to continue, and a new request will go to the back of the line.";
  }
  return 'This will end your current conversation.';
}

interface ConfirmRestartSheetProps {
  status: ChatStatus;
  confirming: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Design's `confirmRestart` sheet (Pollen review, 10/7 — both confirms must
 * match the design, not just the "Talk to a person?" one). Same bottom-sheet
 * treatment as ConfirmHumanSheet, rendered inline in the panel rather than a
 * MUI Dialog.
 */
export const ConfirmRestartSheet: React.FC<ConfirmRestartSheetProps> = ({ status, confirming, error, onConfirm, onCancel }) => (
  <Box
    onClick={backdropClickCancels(confirming, onCancel)}
    sx={{
      position: 'absolute',
      inset: 0,
      bgcolor: 'rgba(5,12,20,0.55)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      zIndex: 3,
    }}
  >
    <Box
      role="dialog"
      aria-label={AI_CHAT_CONFIRM_RESTART_TITLE}
      onClick={(e) => e.stopPropagation()}
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: '16px 16px 0 0',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        boxShadow: '0 -16px 48px rgba(0,0,0,0.35)',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '28px',
          bgcolor: RESTART_ICON_BG,
          color: RESTART_ICON_COLOR,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <RestartAltIcon sx={{ fontSize: 26 }} />
      </Box>
      <Typography sx={{ fontSize: 20, fontWeight: 600, color: HEADING_INK, lineHeight: 1.25 }}>
        {AI_CHAT_CONFIRM_RESTART_TITLE}
      </Typography>
      <Typography sx={{ fontSize: 14.5, lineHeight: 1.5, color: BODY_INK }}>{confirmRestartBody(status)}</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={confirming}
        sx={{
          height: 52,
          mt: 0.75,
          borderRadius: '14px',
          bgcolor: RESTART_ICON_COLOR,
          '&:hover': { bgcolor: RESTART_ICON_COLOR },
          fontSize: 15,
          fontWeight: 600,
          textTransform: 'none',
        }}
      >
        {confirming ? 'Starting…' : AI_CHAT_CONFIRM_RESTART_CONFIRM}
      </Button>
      <Button
        onClick={onCancel}
        disabled={confirming}
        sx={{ height: 48, color: '#5B6B7C', fontSize: 15, fontWeight: 500, borderRadius: '12px', textTransform: 'none' }}
      >
        {AI_CHAT_CONFIRM_RESTART_CANCEL}
      </Button>
    </Box>
  </Box>
);

// ── Header X "Resolve this chat?" confirm sheet ────────────────────────────

export const AI_CHAT_CONFIRM_CLOSE_TITLE = 'Resolve this chat?';
export const AI_CHAT_CONFIRM_CLOSE_BODY = 'You can start a new one any time.';
export const AI_CHAT_CONFIRM_CLOSE_CONFIRM = 'Resolve';
export const AI_CHAT_CONFIRM_CLOSE_CANCEL = 'Keep open';

interface ConfirmResolveSheetProps {
  resolving: boolean;
  error?: string | null;
  onResolve: () => void;
  /** The "Keep open" button — keeps the chat live AND closes the panel, same as today. */
  onKeepOpen: () => void;
  /** Backdrop click or Esc — the user didn't mean to open this at all, so only the sheet goes away; the panel stays open untouched (Carlos, 10/7). */
  onDismiss: () => void;
}

/**
 * Design's check-icon confirm, same `#E8F2FB` circle as ConfirmHumanSheet's
 * headset (Carlos, 10/7 — all 3 confirms must be sheets, none left as a MUI
 * Dialog). Unlike the other two sheets, its backdrop/Esc dismissal is NOT the
 * same action as its own secondary button: "Keep open" deliberately closes
 * the panel (that's what clicking X already asked for), while backdrop/Esc
 * means the X click itself was a mistake and nothing should happen.
 */
export const ConfirmResolveSheet: React.FC<ConfirmResolveSheetProps> = ({
  resolving,
  error,
  onResolve,
  onKeepOpen,
  onDismiss,
}) => {
  return (
    <Box
      onClick={backdropClickCancels(resolving, onDismiss)}
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: 'rgba(5,12,20,0.55)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        zIndex: 3,
      }}
    >
      <Box
        role="dialog"
        aria-label={AI_CHAT_CONFIRM_CLOSE_TITLE}
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: '16px 16px 0 0',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          boxShadow: '0 -16px 48px rgba(0,0,0,0.35)',
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '28px',
            bgcolor: ACCENT_SOFT_BG,
            color: ACCENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckIcon sx={{ fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: HEADING_INK, lineHeight: 1.25 }}>
          {AI_CHAT_CONFIRM_CLOSE_TITLE}
        </Typography>
        <Typography sx={{ fontSize: 14.5, lineHeight: 1.5, color: BODY_INK }}>{AI_CHAT_CONFIRM_CLOSE_BODY}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Button
          variant="contained"
          onClick={onResolve}
          disabled={resolving}
          sx={{
            height: 52,
            mt: 0.75,
            borderRadius: '14px',
            bgcolor: ACCENT,
            '&:hover': { bgcolor: ACCENT },
            fontSize: 15,
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          {resolving ? 'Resolving…' : AI_CHAT_CONFIRM_CLOSE_CONFIRM}
        </Button>
        <Button
          onClick={onKeepOpen}
          disabled={resolving}
          sx={{ height: 48, color: '#5B6B7C', fontSize: 15, fontWeight: 500, borderRadius: '12px', textTransform: 'none' }}
        >
          {AI_CHAT_CONFIRM_CLOSE_CANCEL}
        </Button>
      </Box>
    </Box>
  );
};

// ── Emergency card (web chat "emergency detected" design, Carlos, 10/7) ────

export const AI_CHAT_EMERGENCY_TITLE = 'Emergency?';
export const AI_CHAT_EMERGENCY_CALL_911 = 'Call 911';
export const AI_CHAT_EMERGENCY_BODY =
  "If someone is in danger, call 911 now. We've flagged this chat for our team to review.";

/**
 * Replaces a plain warning Alert for an urgent turn — matches
 * `~/Downloads/Web chat — emergency detected-html/WEmergency.dc.html`
 * exactly: a red-bordered card, a real `tel:911` call button, and the
 * flagged-for-review line. The chat's own auto-escalation (unchanged;
 * already status `escalated` server-side for an urgent turn) still drives
 * the yellow handoff banner below this independently of this card.
 */
export const EmergencyCard: React.FC = () => (
  <Box
    role="alert"
    sx={{
      alignSelf: 'stretch',
      border: `2px solid ${EMERGENCY_BORDER}`,
      bgcolor: EMERGENCY_BG,
      borderRadius: '16px',
      p: '14px 14px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: EMERGENCY_RED, fontSize: 15, fontWeight: 700 }}>
      <WarningAmberIcon sx={{ fontSize: 20 }} />
      {AI_CHAT_EMERGENCY_TITLE}
    </Box>
    <Button
      component="a"
      href="tel:911"
      startIcon={<CallIcon sx={{ fontSize: 20 }} />}
      sx={{
        height: 52,
        borderRadius: '12px',
        bgcolor: EMERGENCY_BORDER,
        '&:hover': { bgcolor: EMERGENCY_BORDER },
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: 700,
        textTransform: 'none',
        boxShadow: '0 6px 16px rgba(217,58,43,0.3)',
      }}
    >
      {AI_CHAT_EMERGENCY_CALL_911}
    </Button>
    <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: BODY_INK, textAlign: 'center' }}>
      {AI_CHAT_EMERGENCY_BODY}
    </Typography>
  </Box>
);
